import { Injectable } from '@angular/core';
import { environment } from '@environments';
import posthog from 'posthog-js';

/** Pending Scenario Abandoned payload kept after Overview is destroyed (e.g. user navigated to Logs). */
export type ScenarioAbandonSnapshot = {
  shipName: string;
  voyageNumber: string;
  factorAdjustedCount: number;
  originalVoyageRiskLevel?: string;
  updatedVoyageRiskLevel?: string;
  originalShipRiskLevel?: string;
  updatedShipRiskLevel?: string;
  pageEnteringTime?: number;
  environment?: string;
};

/**
 * Persists overview scenario-abandon state at app scope so tab/browser close still captures
 * after the portal tab was backgrounded or after in-app navigation away from Overview.
 */
@Injectable({ providedIn: 'root' })
export class ScenarioAbandonTrackingService {
  private static readonly STORAGE_KEY = 'st-a-eye-scenario-abandon';

  private snapshot: ScenarioAbandonSnapshot | null = null;
  private captured = false;
  private listenersInstalled = false;

  constructor() {
    this.installUnloadListeners();
  }

  /** Clear pending abandon state after a successful save or new scenario session. */
  clear(): void {
    this.snapshot = null;
    this.captured = false;
    this.clearStoredSnapshot();
  }

  /** Start a new ship scenario — allows abandon tracking again. */
  resetForNewScenario(): void {
    this.captured = false;
  }

  /** Keep the latest abandon-eligible snapshot while Overview is open or being destroyed. */
  syncPending(snapshot: ScenarioAbandonSnapshot | null): void {
    if (this.captured) {
      return;
    }
    if (!snapshot || snapshot.factorAdjustedCount === 0) {
      this.snapshot = null;
      this.clearStoredSnapshot();
      return;
    }
    this.snapshot = snapshot;
    this.storeSnapshot(snapshot);
  }

  /**
   * Send Scenario Abandoned when the user leaves without saving.
   * Uses sendBeacon so the event survives immediate SPA navigation or tab close.
   */
  tryCapture(): boolean {
    this.ensureSnapshotLoaded();
    if (this.captured || !this.snapshot || this.snapshot.factorAdjustedCount === 0) {
      return false;
    }
    if (!environment.POSTHOG_ENABLED_TRACKING) {
      this.captured = true;
      return true;
    }

    const pageLeavingTime = Date.now();
    const pageEnteringTime = this.snapshot.pageEnteringTime;

    posthog.capture(
      'Scenario Abandoned',
      {
        shipName: this.snapshot.shipName,
        voyageNumber: this.snapshot.voyageNumber,
        factorAdjustedCount: this.snapshot.factorAdjustedCount,
        originalVoyageRiskLevel: this.snapshot.originalVoyageRiskLevel,
        updatedVoyageRiskLevel: this.snapshot.updatedVoyageRiskLevel,
        originalShipRiskLevel: this.snapshot.originalShipRiskLevel,
        updatedShipRiskLevel: this.snapshot.updatedShipRiskLevel,
        timeSpentSeconds:
          pageEnteringTime != null
            ? Math.max(0, Math.round((pageLeavingTime - pageEnteringTime) / 1000))
            : 0,
        environment: this.snapshot.environment,
      },
      { transport: 'sendBeacon', send_instantly: true },
    );

    this.captured = true;
    this.snapshot = null;
    this.clearStoredSnapshot();
    return true;
  }

  private ensureSnapshotLoaded(): void {
    if (this.snapshot) {
      return;
    }
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const raw = sessionStorage.getItem(ScenarioAbandonTrackingService.STORAGE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw) as ScenarioAbandonSnapshot;
      if (parsed.factorAdjustedCount > 0) {
        this.snapshot = parsed;
      }
    } catch {
      this.clearStoredSnapshot();
    }
  }

  private storeSnapshot(snapshot: ScenarioAbandonSnapshot): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      sessionStorage.setItem(
        ScenarioAbandonTrackingService.STORAGE_KEY,
        JSON.stringify(snapshot),
      );
    } catch {
      // Storage full or unavailable — in-memory snapshot still used.
    }
  }

  private clearStoredSnapshot(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    sessionStorage.removeItem(ScenarioAbandonTrackingService.STORAGE_KEY);
  }

  private installUnloadListeners(): void {
    if (typeof window === 'undefined' || this.listenersInstalled) {
      return;
    }
    this.listenersInstalled = true;
    // Bubble phase runs after Overview's capture-phase snapshot refresh on pagehide.
    window.addEventListener('pagehide', this.onPageHide);
  }

  /**
   * Tab/window close (including closing a background portal tab while another tab is focused).
   * Skips bfcache entry (persisted=true) where the user may still return.
   */
  private readonly onPageHide = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      return;
    }
    this.tryCapture();
  };
}
